import { Button } from '@/components/atoms';
import { BackArrowIcon } from '@/components/atoms/BackArrowIcon';
import {
  ChevronRightIcon,
  NoSlotsAvailableIcon,
} from '@/components/atoms/icons';
import { Typography } from '@/components/atoms/Typography';
import { ContentDialog } from '@/components/molecules/dialogs/ContentDialog';
import { SelectionChip } from '@/components/molecules/SelectionChip';
import { UI_PRIMITIVES } from '@/core/constants/uiColors';
import { ALMETLAA_ACTION, ALMETLAA_SCREEN } from '@/core/constants/dynatraceCatalog';
import { useI18nContext, useUIColors } from '@/core/hooks';
import { trackCatalogEvent } from '@/core/services/trackDynatraceCatalog';
import { isPhoneScreen, isSmallScreen } from '@/core/utils/responsive';
import { useRouter } from 'expo-router';
import React, { useEffect, useRef, useState } from 'react';
import {
  Modal,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
  type ViewStyle,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import type { AppointmentLabels } from '../constants/getAppointmentLabels';
import { useAppointmentLabels } from '../hooks/useAppointmentLabels';
import type {
  AppointmentSlotDate,
  AppointmentSlotDialogMode,
  AppointmentSlotsLoadState,
  AppointmentSlotTime,
} from '../types/appointment.types';
import {
  AppointmentTimeSlotsSkeleton,
  TIME_SLOTS_AREA_MIN_HEIGHT_DESKTOP,
  TIME_SLOTS_AREA_MIN_HEIGHT_MOBILE,
} from './skeleton';

export interface AppointmentSlotDialogProps {
  visible: boolean;
  mode: AppointmentSlotDialogMode;
  dates: AppointmentSlotDate[];
  times: AppointmentSlotTime[];
  slotsLoadState?: AppointmentSlotsLoadState;
  initialDateId?: string | null;
  initialTimeId?: string | null;
  /** Optional submit button label (defaults to Book / Reschedule Appointment). */
  submitLabel?: string;
  /** Disable submit while schedule/update is in flight. */
  isSubmitting?: boolean;
  onClose: () => void;
  onSubmit: (payload: { dateId: string; timeId: string }) => void;
  /** Fired when the user selects a different slot date (refetch times). */
  onDateChange?: (dateId: string) => void;
  /**
   * Current booked slot id (upcoming.appointment_id === slots.appointment_slot_id).
   * That chip is disabled on reschedule so the same slot cannot be submitted again.
   */
  bookedSlotId?: string | null;
}

/** Brand color for the "Raise a Support Ticket" link. */
const RAISE_SUPPORT_COLOR = '#922A22';

const TIME_CHIP_WIDTH = 97;
const TIME_COLUMNS_DESKTOP = 6;
const TIME_COLUMNS_MOBILE = 3;
const TIME_GAP = 12;
const SLOT_DIALOG_H_PADDING = 32;
const SLOT_DIALOG_MAX_WIDTH =
  SLOT_DIALOG_H_PADDING * 2 +
  TIME_CHIP_WIDTH * TIME_COLUMNS_DESKTOP +
  TIME_GAP * (TIME_COLUMNS_DESKTOP - 1);
const DATE_PAGE_SIZE_DESKTOP = 7;
const DATE_CHIP_WIDTH = 80;
const DATE_SLIDER_GAP = 12;
const DATE_SLIDER_STEP = DATE_CHIP_WIDTH + DATE_SLIDER_GAP;
const DATE_SLIDER_MOBILE_PAD_LEFT = 16;
/** Desktop viewport shows ~7 chips; chevron pages by that width. */
const DATE_SLIDER_PAGE =
  DATE_CHIP_WIDTH * DATE_PAGE_SIZE_DESKTOP +
  DATE_SLIDER_GAP * (DATE_PAGE_SIZE_DESKTOP - 1);

function getDateSliderOffset(
  dateId: string | null | undefined,
  dates: ReadonlyArray<{ id: string; value?: string }>,
  paddingLeft: number,
): number {
  if (!dateId) {
    return 0;
  }
  const index = dates.findIndex(
    (date) => date.id === dateId || date.value === dateId,
  );
  if (index <= 0) {
    return 0;
  }
  return paddingLeft + index * DATE_SLIDER_STEP;
}

interface SlotTimesPanelProps {
  isMobile: boolean;
  /** Phone (<768px): equal-width 3-column time chips; wider: fixed 97px chips. */
  isPhoneTimeGrid: boolean;
  times: AppointmentSlotTime[];
  slotsLoadState: AppointmentSlotsLoadState;
  selectedTimeId: string | null;
  bookedSlotId?: string | null;
  onSelectTime: (timeId: string) => void;
  onRaiseSupportTicket: () => void;
}

function isSlotTimeDisabled(
  time: AppointmentSlotTime,
  bookedSlotId?: string | null,
): boolean {
  if (time.disabled === true) {
    return true;
  }
  if (!bookedSlotId) {
    return false;
  }
  return time.id === bookedSlotId;
}

function getEqualColumnWidth(
  gridWidth: number,
  columnCount: number,
  gap: number,
): number | undefined {
  if (gridWidth <= 0 || columnCount <= 0) {
    return;
  }
  return (gridWidth - gap * (columnCount - 1)) / columnCount;
}

function submitSelectedSlot(params: Readonly<{
  selectedDateId: string | null;
  selectedTimeId: string | null;
  isSubmitting: boolean;
  bookedSlotId?: string | null;
  onSubmit: (payload: { dateId: string; timeId: string }) => void;
}>) {
  const { selectedDateId, selectedTimeId, isSubmitting, bookedSlotId } =
    params;

  if (!selectedDateId || !selectedTimeId || isSubmitting) {
    return;
  }

  if (bookedSlotId && selectedTimeId === bookedSlotId) {
    return;
  }

  params.onSubmit({ dateId: selectedDateId, timeId: selectedTimeId });
}

function alignSlotDateSlider(params: Readonly<{
  pendingDateAlignX: React.RefObject<number | null>;
  dateScrollRef: React.RefObject<ScrollView | null>;
}>) {
  const offset = params.pendingDateAlignX.current;
  if (offset == null) {
    return;
  }
  params.dateScrollRef.current?.scrollTo({ x: offset, animated: false });
}

function handleDateSliderScroll(params: Readonly<{
  event: NativeSyntheticEvent<NativeScrollEvent>;
  pendingDateAlignX: React.RefObject<number | null>;
  dateScrollX: React.RefObject<number>;
  setDateScrollAtStart: (value: boolean) => void;
  setDateScrollAtEnd: (value: boolean) => void;
}>) {
  const {
    event,
    pendingDateAlignX,
    dateScrollX,
    setDateScrollAtStart,
    setDateScrollAtEnd,
  } = params;

  const { contentOffset, contentSize, layoutMeasurement } = event.nativeEvent;

  dateScrollX.current = contentOffset.x;
  const absX = Math.abs(contentOffset.x);

  const pending = pendingDateAlignX.current;
  if (pending != null && Math.abs(contentOffset.x - pending) <= 2) {
    pendingDateAlignX.current = null;
  }

  setDateScrollAtStart(absX <= 2);
  setDateScrollAtEnd(
    absX + layoutMeasurement.width >= contentSize.width - 2,
  );
}

function handleDateNavBack(params: Readonly<{
  canPageBack: boolean;
  scrollDatesBy: (direction: -1 | 1) => void;
}>) {
  if (!params.canPageBack) {
    return;
  }
  trackCatalogEvent({
    screen: ALMETLAA_SCREEN.APPOINTMENT,
    actionName: ALMETLAA_ACTION.APPOINTMENT.DATE_SCROLL_CLICK,
    eventType: 'action',
  });
  params.scrollDatesBy(-1);
}

function handleDateNavForward(params: Readonly<{
  canPageForward: boolean;
  scrollDatesBy: (direction: -1 | 1) => void;
}>) {
  if (!params.canPageForward) {
    return;
  }
  trackCatalogEvent({
    screen: ALMETLAA_SCREEN.APPOINTMENT,
    actionName: ALMETLAA_ACTION.APPOINTMENT.DATE_SCROLL_CLICK,
    eventType: 'action',
  });
  params.scrollDatesBy(1);
}

function handleDatePressChip(params: Readonly<{
  date: AppointmentSlotDate;
  selectedDateId: string | null;
  setSelectedDateId: (value: string | null) => void;
  setSelectedTimeId: (value: string | null) => void;
  onDateChange: ((dateId: string) => void) | undefined;
}>) {
  if (params.date.disabled === true) {
    return;
  }

  // Date → slots refetch is web-only (native BookAppointmentScreen owns its own flow).
  if (Platform.OS !== 'web') {
    params.setSelectedDateId(params.date.id);
    trackCatalogEvent({
      screen: ALMETLAA_SCREEN.APPOINTMENT,
      actionName: ALMETLAA_ACTION.APPOINTMENT.DATE_SELECT_CLICK,
      eventType: 'action',
    });
    return;
  }

  if (params.selectedDateId === params.date.id) {
    return;
  }

  params.setSelectedDateId(params.date.id);
  params.setSelectedTimeId(null);
  trackCatalogEvent({
    screen: ALMETLAA_SCREEN.APPOINTMENT,
    actionName: ALMETLAA_ACTION.APPOINTMENT.DATE_SELECT_CLICK,
    eventType: 'action',
  });
  params.onDateChange?.(params.date.id);
}

function AppointmentDateSection(params: Readonly<{
  isMobile: boolean;
  dates: AppointmentSlotDate[];
  hasNoDates: boolean;
  canPageBack: boolean;
  canPageForward: boolean;
  onDateNavBack: () => void;
  onDateNavForward: () => void;
  onDateSliderScroll: (
    event: NativeSyntheticEvent<NativeScrollEvent>,
  ) => void;
  dateScrollRef: React.RefObject<ScrollView | null>;
  pendingDateAlignX: React.RefObject<number | null>;
  selectedDateId: string | null;
  onDatePress: (date: AppointmentSlotDate) => void;
  onRaiseSupportTicket: () => void;
  colors: ReturnType<typeof useUIColors>;
}>) {
  const labels = useAppointmentLabels();
  const { isRTL } = useI18nContext();
  const isWeb = Platform.OS === 'web';
  return (
    <View style={styles.section}>
      <View style={styles.sectionHeader}>
        <Typography
          variant="headline"
          color="secondary"
          style={styles.sectionLabel}
        >
          {labels.SLOT_DATE}
        </Typography>

        {!params.hasNoDates ? (
          <DateSliderNav
            canPageBack={params.canPageBack}
            canPageForward={params.canPageForward}
            onDateNavBack={params.onDateNavBack}
            onDateNavForward={params.onDateNavForward}
            colors={params.colors}
            isRTL={isRTL}
          />
        ) : null}
      </View>

      {params.hasNoDates ? (
        <View style={styles.noDatesContent}>
          <Typography variant="body" color="secondary" style={styles.stateText}>
            {labels.SLOTS_NONE_AVAILABLE}
          </Typography>
          <Pressable onPress={params.onRaiseSupportTicket}>
            <Typography
              variant={isWeb ? 'body' : 'headline'}
              style={
                isWeb
                  ? styles.raiseSupportTicketTextWeb
                  : [
                      styles.raiseSupportTicketText,
                      { color: RAISE_SUPPORT_COLOR },
                    ]
              }
            >
              {labels.RAISE_SUPPORT_TICKET}
            </Typography>
          </Pressable>
        </View>
      ) : (
        <DateSliderList
          dates={params.dates}
          isMobile={params.isMobile}
          selectedDateId={params.selectedDateId}
          onDatePress={params.onDatePress}
          dateScrollRef={params.dateScrollRef}
          pendingDateAlignX={params.pendingDateAlignX}
          onDateSliderScroll={params.onDateSliderScroll}
          isRTL={isRTL}
        />
      )}
    </View>
  );
}

function initDateSliderOnVisible(params: Readonly<{
  visible: boolean;
  initialDateId: string | null;
  initialTimeId: string | null;
  isMobile: boolean;
  dates: AppointmentSlotDate[];
  dateScrollX: React.RefObject<number>;
  pendingDateAlignX: React.RefObject<number | null>;
  dateScrollRef: React.RefObject<ScrollView | null>;
  setSelectedDateId: (value: string | null) => void;
  setSelectedTimeId: (value: string | null) => void;
  setDateScrollAtStart: (value: boolean) => void;
  isRTL: boolean;
  setDateScrollAtEnd: (value: boolean) => void;
}>) {
  if (!params.visible) {
    return;
  }

  params.setSelectedDateId(params.initialDateId);
  params.setSelectedTimeId(params.initialTimeId);

  const paddingLeft = params.isMobile ? DATE_SLIDER_MOBILE_PAD_LEFT : 0;
  let offset = getDateSliderOffset(
    params.initialDateId,
    params.dates,
    paddingLeft,
  );

  if (Platform.OS === 'web' && params.isRTL) {
    offset = -offset;
  }

  params.dateScrollX.current = offset;
  params.pendingDateAlignX.current = offset;
  params.setDateScrollAtStart(Math.abs(offset) <= 2);
  params.setDateScrollAtEnd(false);

  // Dialog/ScrollView may not be laid out on the first frame (web modal).
  const rafId = requestAnimationFrame(() =>
    alignSlotDateSlider({
      pendingDateAlignX: params.pendingDateAlignX,
      dateScrollRef: params.dateScrollRef,
    }),
  );

  const timeoutId = setTimeout(
    () =>
      alignSlotDateSlider({
        pendingDateAlignX: params.pendingDateAlignX,
        dateScrollRef: params.dateScrollRef,
      }),
    50,
  );

  return () => {
    cancelAnimationFrame(rafId);
    clearTimeout(timeoutId);
  };
}

function syncSelectedTimeWhenSlotsReady(params: Readonly<{
  slotsLoadState: AppointmentSlotsLoadState;
  selectedTimeId: string | null;
  times: AppointmentSlotTime[];
  bookedSlotId?: string | null;
  setSelectedTimeId: (value: string | null) => void;
}>) {
  if (params.slotsLoadState !== 'success' || !params.selectedTimeId) {
    return;
  }

  const selected = params.times.find((time) => time.id === params.selectedTimeId);
  if (!selected || isSlotTimeDisabled(selected, params.bookedSlotId)) {
    params.setSelectedTimeId(null);
  }
}

function EmptySlotsContent({
  isRTL,
  labels,
  colors,
  onRaiseSupportTicket,
}: Readonly<{
  isRTL: boolean;
  labels: ReturnType<typeof useAppointmentLabels>;
  colors: ReturnType<typeof useUIColors>;
  onRaiseSupportTicket: () => void;
}>) {
  const isWeb = Platform.OS === 'web';
  return (
    <View style={styles.stateBlock}>
      <View style={styles.emptySlotsContent}>
        <NoSlotsAvailableIcon size={60} />
        <View
          style={[
            styles.emptySlotsMessageRow,
            isWeb ? styles.emptySlotsMessageRowWeb : null,
            isRTL ? styles.emptySlotsMessageRowRtl : null,
          ]}
        >
          <Typography
            variant={isWeb ? 'body' : 'headline'}
            color={isWeb ? 'secondary' : 'primary'}
            style={[
              styles.emptySlotsText,
              isWeb ? styles.emptySlotsTextWeb : null,
            ]}
          >
            {labels.SLOTS_EMPTY}
          </Typography>
          <Pressable onPress={onRaiseSupportTicket}>
            <Typography
              variant={isWeb ? 'body' : 'headline'}
              style={
                isWeb
                  ? styles.raiseSupportTicketTextWeb
                  : [
                      styles.raiseSupportTicketText,
                      { color: RAISE_SUPPORT_COLOR },
                    ]
              }
            >
              {labels.RAISE_SUPPORT_TICKET}
            </Typography>
          </Pressable>
        </View>
      </View>
    </View>
  );
}

function SuccessSlotsContent({
  isPhoneTimeGrid,
  times,
  selectedTimeId,
  bookedSlotId,
  mobileChipWidth,
  onSelectTime,
  setGridWidth,
}: Readonly<{
  isPhoneTimeGrid: boolean;
  times: AppointmentSlotTime[];
  selectedTimeId: string | null;
  bookedSlotId?: string | null | undefined;
  mobileChipWidth: number | undefined;
  onSelectTime: (timeId: string) => void;
  setGridWidth: React.Dispatch<React.SetStateAction<number>>;
}>) {
  return (
    <View
      style={[
        styles.timeGrid,
        isPhoneTimeGrid && styles.timeGridMobile,
      ]}
      onLayout={(event) => {
        const nextWidth = event.nativeEvent.layout.width;
        setGridWidth((current) => (current === nextWidth ? current : nextWidth));
      }}
    >
      {times.map((time) => {
        const disabled = isSlotTimeDisabled(time, bookedSlotId);
        return (
          <View
            key={time.id}
            style={
              isPhoneTimeGrid
                ? [
                    styles.timeChipWrapMobile,
                    mobileChipWidth ? { width: mobileChipWidth } : null,
                  ]
                : styles.timeChipWrap
            }
          >
            <SelectionChip
              size={isPhoneTimeGrid ? 'timeMobile' : 'time'}
              label={time.label}
              selected={selectedTimeId === time.id}
              onPress={() => onSelectTime(time.id)}
              disabled={disabled}
            />
          </View>
        );
      })}
    </View>
  );
}

function SlotTimesPanel({
  isMobile,
  isPhoneTimeGrid,
  times,
  slotsLoadState,
  selectedTimeId,
  bookedSlotId,
  onSelectTime,
  onRaiseSupportTicket,
}: Readonly<SlotTimesPanelProps>) {
  const colors = useUIColors();
  const { isRTL } = useI18nContext();
  const labels = useAppointmentLabels();
  const [gridWidth, setGridWidth] = useState(0);
  const mobileChipWidth = getEqualColumnWidth(
    gridWidth,
    TIME_COLUMNS_MOBILE,
    TIME_GAP,
  );
  const timeAreaStyle = [
    styles.timeArea,
    isMobile ? styles.timeAreaMobile : null,
  ];
  let content: React.ReactNode = null;

  if (slotsLoadState === 'loading' || slotsLoadState === 'idle') {
    content = <AppointmentTimeSlotsSkeleton isMobile={isPhoneTimeGrid} />;
  } else if (slotsLoadState === 'error') {
    content = (
      <View style={styles.stateBlock}>
        <Typography
          variant="body"
          color="secondary"
          style={styles.stateText}
        >
          {labels.SLOTS_ERROR}
        </Typography>
      </View>
    );
  } else if (slotsLoadState === 'empty') {
    content = (
      <EmptySlotsContent
        isRTL={isRTL}
        labels={labels}
        colors={colors}
        onRaiseSupportTicket={onRaiseSupportTicket}
      />
    );
  } else if (slotsLoadState === 'success') {
    content = (
      <SuccessSlotsContent
        isPhoneTimeGrid={isPhoneTimeGrid}
        times={times}
        selectedTimeId={selectedTimeId}
        bookedSlotId={bookedSlotId}
        mobileChipWidth={mobileChipWidth}
        onSelectTime={onSelectTime}
        setGridWidth={setGridWidth}
      />
    );
  }

  return (
    <View style={styles.section}>
      <Typography
        variant="headline"
        color="primary"
        style={styles.sectionLabel}
      >
        {labels.SLOT_TIME}
      </Typography>
      <View style={timeAreaStyle}>{content}</View>
    </View>
  );
}

function getSlotDialogTitle(isBook: boolean, labels: AppointmentLabels): string {
  if (isBook) {
    return labels.BOOK_TITLE;
  }
  return labels.RESCHEDULE_TITLE;
}

function getDefaultSubmitLabel(
  isBook: boolean,
  isMobile: boolean,
  labels: AppointmentLabels,
): string {
  if (isBook) {
    return labels.BOOK_CTA;
  }
  if (isMobile) {
    return labels.RESCHEDULE_CTA_MOBILE;
  }
  return labels.RESCHEDULE_CTA;
}

function getCanSubmitSlot(params: {
  mode: AppointmentSlotDialogMode;
  slotsReady: boolean;
  hasSelection: boolean;
  selectionChanged: boolean;
  selectedTimeId: string | null;
  bookedSlotId?: string | null;
}): boolean {
  if (!params.slotsReady || !params.hasSelection) {
    return false;
  }
  if (
    params.bookedSlotId != null &&
    params.bookedSlotId.length > 0 &&
    params.selectedTimeId === params.bookedSlotId
  ) {
    return false;
  }
  if (params.mode === 'reschedule') {
    return params.selectionChanged;
  }
  return true;
}

/**
 * Book / Reschedule slot picker.
 * Desktop: centered dialog. Mobile: full-screen (not a bottom sheet).
 */
export function AppointmentSlotDialog({
  visible,
  mode,
  dates,
  times,
  slotsLoadState = 'idle',
  initialDateId = null,
  initialTimeId = null,
  submitLabel: submitLabelProp,
  isSubmitting = false,
  onClose,
  onSubmit,
  onDateChange,
  bookedSlotId = null,
}: Readonly<AppointmentSlotDialogProps>) {
  const colors = useUIColors();
  const labels = useAppointmentLabels();
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const isMobile = isSmallScreen();
  const isPhoneTimeGrid = isPhoneScreen();
  const { isRTL } = useI18nContext();
  const dateScrollRef = useRef<ScrollView>(null);
  const dateScrollX = useRef(0);
  const pendingDateAlignX = useRef<number | null>(null);
  const [dateScrollAtStart, setDateScrollAtStart] = useState(true);
  const [dateScrollAtEnd, setDateScrollAtEnd] = useState(false);

  const [selectedDateId, setSelectedDateId] = useState<string | null>(null);
  const [selectedTimeId, setSelectedTimeId] = useState<string | null>(null);

  useEffect(() => {
    return initDateSliderOnVisible({
      visible,
      initialDateId,
      initialTimeId,
      isMobile,
      dates,
      dateScrollX,
      pendingDateAlignX,
      dateScrollRef,
      setSelectedDateId,
      setSelectedTimeId,
      setDateScrollAtStart,
      setDateScrollAtEnd,
      isRTL,
    });
  }, [visible, initialDateId, initialTimeId, dates, isMobile, isRTL]);

  useEffect(() => {
    syncSelectedTimeWhenSlotsReady({
      slotsLoadState,
      selectedTimeId,
      times,
      bookedSlotId,
      setSelectedTimeId,
    });
  }, [slotsLoadState, times, selectedTimeId, bookedSlotId]);

  const hasSelection = Boolean(selectedDateId && selectedTimeId);
  const selectionChanged =
    selectedDateId !== initialDateId || selectedTimeId !== initialTimeId;
  const slotsReady = slotsLoadState === 'success' && times.length > 0;
  const canSubmit = getCanSubmitSlot({
    mode,
    slotsReady,
    hasSelection,
    selectionChanged,
    selectedTimeId,
    bookedSlotId,
  }) && !isSubmitting;
  const isBook = mode === 'book';
  const title = getSlotDialogTitle(isBook, labels);
  const submitLabel =
    submitLabelProp ?? getDefaultSubmitLabel(isBook, isMobile, labels);

  const canPageBack = !dateScrollAtStart;
  const canPageForward = !dateScrollAtEnd;

  const handleSubmit = () => {
    submitSelectedSlot({
      selectedDateId,
      selectedTimeId,
      isSubmitting,
      bookedSlotId,
      onSubmit,
    });
  };

  const handleCancelSlotDialog = () => {
    trackCatalogEvent({
      screen: ALMETLAA_SCREEN.APPOINTMENT,
      actionName: ALMETLAA_ACTION.APPOINTMENT.SLOT_DIALOG_CANCEL,
      eventType: 'action',
    });
    onClose();
  };

  const onDateSliderScroll = (
    event: NativeSyntheticEvent<NativeScrollEvent>,
  ) => {
    handleDateSliderScroll({
      event,
      pendingDateAlignX,
      dateScrollX,
      setDateScrollAtStart,
      setDateScrollAtEnd,
    });
  };

  const scrollDatesBy = (direction: -1 | 1) => {
    pendingDateAlignX.current = null;
    const step = isMobile ? DATE_SLIDER_STEP : DATE_SLIDER_PAGE;
    
    let nextX;
    if (Platform.OS === 'web' && isRTL) {
      nextX = Math.min(0, dateScrollX.current - direction * step);
    } else {
      nextX = Math.max(0, dateScrollX.current + direction * step);
    }

    dateScrollX.current = nextX;
    dateScrollRef.current?.scrollTo({ x: nextX, animated: true });
  };

  const onDateNavBack = () => {
    handleDateNavBack({ canPageBack, scrollDatesBy });
  };

  const onDateNavForward = () => {
    handleDateNavForward({ canPageForward, scrollDatesBy });
  };

  const onDatePress = (date: AppointmentSlotDate) => {
    handleDatePressChip({
      date,
      selectedDateId,
      setSelectedDateId,
      setSelectedTimeId,
      onDateChange,
    });
  };

  const hasNoDates = dates.length === 0;
  const handleRaiseSupportTicket = () => {
    onClose();
    router.push('/service-request-create' as never);
  };

  const dateSection = (
    <AppointmentDateSection
      isMobile={isMobile}
      dates={dates}
      hasNoDates={hasNoDates}
      canPageBack={canPageBack}
      canPageForward={canPageForward}
      onDateNavBack={onDateNavBack}
      onDateNavForward={onDateNavForward}
      onDateSliderScroll={onDateSliderScroll}
      dateScrollRef={dateScrollRef}
      pendingDateAlignX={pendingDateAlignX}
      selectedDateId={selectedDateId}
      onDatePress={onDatePress}
      onRaiseSupportTicket={handleRaiseSupportTicket}
      colors={colors}
    />
  );

  const timeSection = (
    <SlotTimesPanel
      isMobile={isMobile}
      isPhoneTimeGrid={isPhoneTimeGrid}
      times={times}
      slotsLoadState={slotsLoadState}
      selectedTimeId={selectedTimeId}
      bookedSlotId={bookedSlotId}
          onSelectTime={(timeId) => {
            trackCatalogEvent({
              screen: ALMETLAA_SCREEN.APPOINTMENT,
              actionName: ALMETLAA_ACTION.APPOINTMENT.SLOT_SELECT_CLICK,
              eventType: 'action',
            });
            setSelectedTimeId(timeId);
          }}
      onRaiseSupportTicket={handleRaiseSupportTicket}
    />
  );

  if (isMobile) {
    return (
      <Modal
        visible={visible}
        animationType="slide"
        presentationStyle="fullScreen"
        onRequestClose={handleCancelSlotDialog}
      >
        <View
          style={[
            styles.mobileRoot,
            {
              backgroundColor: colors.backgroundLevel2,
              paddingTop: insets.top,
              paddingBottom: Math.max(insets.bottom, 16),
            },
            Platform.OS === 'web' ? { direction: isRTL ? 'rtl' : 'ltr' } as ViewStyle : null
          ]}
        >
          <View style={styles.mobileHeader}>
            <Pressable
              onPress={handleCancelSlotDialog}
              style={styles.mobileBackBtn}
              accessibilityRole="button"
              accessibilityLabel="Go back"
            >
              <BackArrowIcon size={24} color={colors.fontPrimary} />
            </Pressable>
            <Typography
              variant="headline"
              color="primary"
              style={styles.mobileTitle}
            >
              {title}
            </Typography>
            <View style={styles.mobileHeaderSpacer} />
          </View>

          <ScrollView
            style={styles.mobileScroll}
            contentContainerStyle={styles.mobileScrollContent}
            showsVerticalScrollIndicator={false}
          >
            {dateSection}
            {hasNoDates ? null : timeSection}
          </ScrollView>

          <View style={[
            styles.mobileFooter,
          ]}>
            <Button
              title={labels.CANCEL}
              variant="outline"
              style={styles.mobileFooterBtn}
              textStyle={styles.actionLabel}
              onPress={handleCancelSlotDialog}
            />
            <Button
              title={submitLabel}
              variant="primary"
              disabled={!canSubmit}
              style={styles.mobileFooterBtn}
              textStyle={[
                styles.actionLabel,
                !canSubmit ? { color: colors.white } : null,
              ]}
              onPress={handleSubmit}
            />
          </View>
        </View>
      </Modal>
    );
  }

  return (
    <ContentDialog
      visible={visible}
      onClose={handleCancelSlotDialog}
      title={title}
      subtitle={labels.SLOT_SUBTITLE}
      titleVariant="title2"
      titleColor="primary"
      subtitleVariant="body"
      subtitleColor="secondary"
      maxWidth={SLOT_DIALOG_MAX_WIDTH}
      cardStyle={styles.dialogCard}
      headerStyle={styles.dialogHeader}
      actionsStyle={styles.dialogActions}
      closeOnBackdropPress={false}
      actions={[
        {
          label: labels.CANCEL,
          variant: 'outline',
          onPress: handleCancelSlotDialog,
          textStyle: styles.actionLabel,
        },
        {
          label: submitLabel,
          variant: 'primary',
          disabled: !canSubmit,
          textStyle: [
            styles.actionLabel,
            !canSubmit ? { color: colors.white } : null,
          ],
          onPress: handleSubmit,
        },
      ]}
    >
      <View style={styles.sections}>
        {dateSection}
        {hasNoDates ? null : timeSection}
      </View>
    </ContentDialog>
  );
}

const styles = StyleSheet.create({
  dialogCard: {
    paddingTop: 40,
    paddingBottom: 32,
    paddingHorizontal: SLOT_DIALOG_H_PADDING,
    gap: 32,
    minHeight: 520,
  },
  /** Keep footer pinned to card bottom while body height changes (shimmer → slots). */
  dialogActions: {
    marginTop: 'auto',
  },
  dialogHeader: {
    gap: 16,
  },
  sections: {
    gap: 28,
    marginTop: -12,
  },
  section: {
    gap: 16,
  },
  stateBlock: {
    flex: 1,
    width: '100%',
    minHeight: TIME_SLOTS_AREA_MIN_HEIGHT_DESKTOP,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 16,
  },
  stateText: {
    lineHeight: 20,
    textAlign: 'center',
    maxWidth: 300,
  },
  noDatesContent: {
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingTop: 8,
  },
  emptySlotsContent: {
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
  },
  emptySlotsMessageRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
    justifyContent: 'center',
  },
  /** Web override: stack text + link vertically. */
  emptySlotsMessageRowWeb: {
    flexDirection: 'column',
    flexWrap: 'nowrap',
    gap: 8,
  },
  emptySlotsMessageRowRtl: {
    flexDirection: 'row-reverse',
  },
  emptySlotsText: {
    textAlign: 'center',
  },
  /** Web override: 13px regular. */
  emptySlotsTextWeb: {
    fontSize: 13,
    fontFamily: 'Matter-Regular',
    fontWeight: '400',
  },
  raiseSupportTicketText: {
    fontFamily: 'Matter-SemiBold',
    fontWeight: '600',
  },
  /** Web override: 14px medium, underlined. */
  raiseSupportTicketTextWeb: {
    fontSize: 14,
    fontFamily: 'Matter-Medium',
    fontWeight: '500',
    color: RAISE_SUPPORT_COLOR,
  },
  /** Reserve 2 real chip rows so footer actions don't jump during loading/empty. */
  timeArea: {
    minHeight: TIME_SLOTS_AREA_MIN_HEIGHT_DESKTOP,
    width: '100%',
    justifyContent: 'flex-start',
  },
  timeAreaMobile: {
    minHeight: TIME_SLOTS_AREA_MIN_HEIGHT_MOBILE,
  },
  sectionLabel: {
    lineHeight: 16,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  navRow: {
    flexDirection: 'row',
    gap: 8,
  },
  navBtn: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
  },
  navIconFlip: {
    transform: [{ scaleX: -1 }],
  },
  dateSliderMobile: {
    marginHorizontal: -16,
  },
  dateSliderDesktop: {
    width: '100%',
    overflow: 'hidden',
  },
  dateSliderContent: {
    flexDirection: 'row',
    gap: DATE_SLIDER_GAP,
    paddingLeft: 16,
    // Leave little trailing pad so the next chip peeks / cuts at the edge
    paddingRight: 8,
  },
  dateSliderContentDesktop: {
    paddingLeft: 0,
    paddingRight: 0,
  },
  dateChipWrap: {
    width: DATE_CHIP_WIDTH,
  },
  timeGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: TIME_GAP,
    width: '100%',
  },
  timeGridMobile: {
    gap: TIME_GAP,
    justifyContent: 'flex-start',
  },
  timeChipWrap: {
    width: TIME_CHIP_WIDTH,
  },
  timeChipWrapMobile: {
    width: '31%',
    flexGrow: 0,
    flexShrink: 0,
  },
  actionLabel: {
    lineHeight: 16,
  },
  mobileRoot: {
    flex: 1,
  },
  mobileHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 12,
    minHeight: 52,
  },
  mobileBackBtn: {
    width: 44,
    height: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  mobileTitle: {
    flex: 1,
    textAlign: 'center',
    lineHeight: 20,
  },
  mobileHeaderSpacer: {
    width: 44,
  },
  mobileScroll: {
    flex: 1,
  },
  mobileScrollContent: {
    paddingHorizontal: 16,
    paddingTop: 8,
    paddingBottom: 24,
    gap: 28,
    overflow: 'hidden',
  },
  mobileFooter: {
    flexDirection: 'row',
    gap: 12,
    paddingHorizontal: 16,
    paddingTop: 12,
  },
  mobileFooterBtn: {
    flex: 1,
    height: 48,
    borderRadius: 4,
  },
});

export default AppointmentSlotDialog;

function DateSliderNav({
  canPageBack,
  canPageForward,
  onDateNavBack,
  onDateNavForward,
  colors,
  isRTL,
}: any) {
  return (
    <View style={styles.navRow}>
      <Pressable
        onPress={onDateNavBack}
        disabled={!canPageBack}
        style={[styles.navBtn, { backgroundColor: UI_PRIMITIVES.greyEEEE }]}
        accessibilityRole="button"
        accessibilityLabel="Previous dates"
      >
        <View style={[styles.navIconFlip, isRTL && { transform: [{ scaleX: 1 }] }]}>
          <ChevronRightIcon
            size={16}
            color={canPageBack ? colors.fontPrimary : UI_PRIMITIVES.white60}
          />
        </View>
      </Pressable>

      <Pressable
        onPress={onDateNavForward}
        disabled={!canPageForward}
        style={[styles.navBtn, { backgroundColor: UI_PRIMITIVES.greyEEEE }]}
        accessibilityRole="button"
        accessibilityLabel="Next dates"
      >
        <View style={[isRTL && styles.navIconFlip]}>
          <ChevronRightIcon
            size={16}
            color={canPageForward ? colors.fontPrimary : UI_PRIMITIVES.white60}
          />
        </View>
      </Pressable>
    </View>
  );
}

function DateSliderList({
  dates,
  isMobile,
  selectedDateId,
  onDatePress,
  dateScrollRef,
  pendingDateAlignX,
  onDateSliderScroll,
  isRTL,
}: any) {
  return (
    <ScrollView
      ref={dateScrollRef}
      horizontal
      showsHorizontalScrollIndicator={false}
      onScroll={onDateSliderScroll}
      onContentSizeChange={() =>
        alignSlotDateSlider({ pendingDateAlignX, dateScrollRef })
      }
      scrollEventThrottle={16}
      decelerationRate="fast"
      snapToInterval={DATE_SLIDER_STEP}
      snapToAlignment="start"
      disableIntervalMomentum
      contentContainerStyle={[
        styles.dateSliderContent,
        !isMobile && styles.dateSliderContentDesktop,
      ]}
      style={[
        isMobile ? styles.dateSliderMobile : styles.dateSliderDesktop,
        Platform.OS === 'web'
          ? ({ scrollBehavior: 'smooth', direction: isRTL ? 'rtl' : 'ltr' } as ViewStyle)
          : null,
      ]}
    >
      {dates.map((date: any) => (
        <View key={date.id} style={styles.dateChipWrap}>
          <SelectionChip
            size="date"
            label={date.label}
            {...(date.sublabel ? { sublabel: date.sublabel } : {})}
            selected={selectedDateId === date.id}
            disabled={date.disabled === true}
            onPress={() => onDatePress(date)}
          />
        </View>
      ))}
    </ScrollView>
  );
}
